import React from 'react';
import {
  Button, Select, TextInput, Textarea, EmptyState, ErrorState, Alert,
  FilterChip, IconButton,
} from '@parsina/framework';
import { OrdersPaneSkeleton } from '../Visit.skeleton.jsx';
import { Check, ClipboardList, Microscope, Scan, Sparkles, X } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { autoDir, useTranslation } from '../../../i18n/I18nProvider.jsx';
import { ORDER_TYPES, ORDER_URGENCIES, labelFor } from '../Visit.shared.js';

// §3.2.2 — دستورات.
//
//   "Constraint: Scope restricted to Dental workflows."
//   "Laboratory Orders: Dental-specific lab requests."
//   "Dental Imaging Orders: X-rays, OPG, CBCT, etc."
//   "Note: Exclude general medical orders unless specific clinical exceptions
//    are defined."
//
// The exclusion is enforced in the BFF, not here: `dentalOrderCatalog` serves a
// curated dental list plus the clinic's OWN declared rows, and `createVisitOrder`
// refuses any order type but lab and imaging. This pane could not offer a CBC
// even if it tried, which is the right place for that rule — a client-side
// filter is a suggestion.
//
// ── The shape of the form is نسخه's, deliberately ──────────────────────────
// §4: "Do not reinvent interaction patterns. If the Doctor App uses a specific
// pattern for Adding a Medication, use that exact pattern for Adding a Dental
// Order." So: pick from a catalogue, fill in the qualifiers beside it, commit
// explicitly. Type tabs first because a dentist knows whether they are sending
// a lab case or asking for a film before they know which one.

const TYPE_ICON = { imaging: Scan, dental_lab: Microscope, lab: Microscope };

const STATUS_TONE = {
  pending: { bg: CT.warnSoft, ink: CT.warnInk },
  in_progress: { bg: CT.accentSoft, ink: CT.accentInk },
  completed: { bg: CT.goodSoft, ink: CT.goodInk },
  cancelled: { bg: CT.surface2, ink: C.textSecondary },
};

/** Which transitions the service will accept from a given status. */
const TRANSITIONS = {
  pending: ['start', 'cancel'],
  in_progress: ['complete'],
  completed: [],
  cancelled: [],
};

export default function OrdersPane({ orders, editable }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const {
    orders: rows, catalog, loading, error, saving, create, setStatus, reload,
    suggestions, suggesting, suggestError, suggest, clearSuggestions,
  } = orders;

  const [type, setType] = React.useState('imaging');
  const [draft, setDraft] = React.useState({
    catalogId: '', description: '', urgency: 'routine', notes: '', sessionCount: '',
  });
  const [failure, setFailure] = React.useState(null);
  // Which suggestion is mid-write. Per-key rather than a boolean: a dentist who
  // presses two in a row must see which one is being filed.
  const [addingKey, setAddingKey] = React.useState(null);

  const forType = React.useMemo(
    () => catalog.filter((c) => c.orderType === type),
    [catalog, type],
  );

  const chosen = forType.find((c) => c.id === draft.catalogId) || null;

  const submit = async () => {
    setFailure(null);
    // The catalogue entry's name IS the description unless the dentist wrote
    // one: an order that reads «رادیوگرافی پانورامیک (OPG)» is what a radiology
    // unit needs, and making them retype it is how it ends up blank.
    const description = draft.description.trim() || chosen?.name || '';
    if (!description) return;
    const sessions = Number(draft.sessionCount);
    const err = await create({
      orderType: type,
      description,
      catalogId: draft.catalogId || null,
      urgency: draft.urgency,
      notes: draft.notes || null,
      code: chosen?.code || null,
      sessionCount: Number.isFinite(sessions) && sessions > 0 ? sessions : null,
    });
    if (err) {
      setFailure(err);
      return;
    }
    setDraft({ catalogId: '', description: '', urgency: 'routine', notes: '', sessionCount: '' });
  };

  // A suggestion becomes an order through the SAME mutation the form calls,
  // with the catalogue row's own values — the BFF matched the name to this
  // clinic's dental catalogue, so `catalogId` is real and accounting can settle
  // against it. The AI's Persian reason rides along as the order's note: it is
  // what tells the radiology unit which tooth, and dropping it would leave the
  // lab a bare «روکش».
  const addSuggested = async (item) => {
    if (addingKey) return;
    setFailure(null);
    setAddingKey(item.key);
    const err = await create({
      orderType: item.orderType,
      description: item.name,
      catalogId: item.catalogId,
      urgency: item.urgency,
      notes: item.reason || null,
      code: item.code || null,
      sessionCount: null,
    });
    if (err) setFailure(err);
    setAddingKey(null);
  };

  const move = async (orderId, transition) => {
    setFailure(null);
    const err = await setStatus(orderId, transition);
    if (err) setFailure(err);
  };

  // The pane's own shape — the type chips, the add form, the order rows —
  // never a centred spinner (DESIGN.md §"Loading & skeleton").
  if (loading) {
    return <OrdersPaneSkeleton label={t('common.loading')} />;
  }

  if (error) {
    return (
      <div role="alert">
        <ErrorState
          title={t('visit.orders.error.title')}
          description={t('visit.orders.error.body')}
          onRetry={reload}
          retryLabel={t('common.actions.retry')}
        />
      </div>
    );
  }

  return (
    <div data-testid="visit-orders" data-state={rows.length ? 'content' : 'empty'}>
      {failure && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          {failure.message || t('visit.orders.saveFailed')}
        </Alert>
      )}

      {editable && (
        <div style={{
          border: `1px solid ${C.border}`, borderRadius: 10,
          padding: 12, marginBottom: 14, background: CT.surface2,
        }}>
          {/* Type first: a dentist knows whether they are sending a lab case or
              asking for a film before they know which one.

              A GROUP of toggle buttons, not a tablist. FilterChip already
              reports `aria-pressed`, which is the right semantic for a toggle —
              and `aria-pressed` is not permitted on `role="tab"`, so declaring
              both (as this did) ships a control with contradictory state. The
              chips also switch a form's options rather than swapping a panel,
              which is not what a tablist means.

              The chip row carries a VISIBLE label, like every other field in
              this form: two unlabelled pills above a «خدمت» dropdown read as
              stray buttons, not as the control that decides what the dropdown
              contains.

              And the selected chip is FILLED, not tinted. FilterChip's own
              `subtle` active state is `bg-navy/5` against a white `outline` —
              a 5% wash that, on a reception monitor at an angle, is no
              difference at all, so the type this pane opens on (تصویربرداری)
              looked exactly as unselected as لابراتوار دندانی. Solid navy plus
              a tick mark says it in two ways at once: colour alone is not a
              state anyone should have to squint at, and it is not a state a
              colour-blind dentist can read at all. */}
          <span
            id="visit-orders-type-label"
            className="select-none"
            style={{
              display: 'block', marginBottom: 6, fontSize: 11.5, fontWeight: 600,
              letterSpacing: '0.01em', lineHeight: 1, color: C.textSecondary,
            }}
          >
            {t('visit.orders.type')}
          </span>
          <div
            role="group"
            aria-labelledby="visit-orders-type-label"
            style={{ display: 'flex', gap: 6, marginBottom: 10 }}
          >
            {ORDER_TYPES.map((ty) => {
              const Icon = TYPE_ICON[ty];
              const active = type === ty;
              return (
                <FilterChip
                  key={ty}
                  active={active}
                  onClick={() => { setType(ty); setDraft((d) => ({ ...d, catalogId: '' })); }}
                  className={active
                    ? 'bg-navy text-white border-navy hover:bg-navy-dark shadow-sm'
                    : 'text-foreground'}
                  label={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {active
                        ? <Check size={14} aria-hidden strokeWidth={3} />
                        : <Icon size={14} aria-hidden />}
                      {t(`visit.orders.types.${ty}`)}
                    </span>
                  }
                />
              );
            })}
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10,
          }}>
            {/* The list is scoped to the chip above — said out loud, because a
                dropdown that silently changes contents is how a dentist ends up
                hunting for OPG in the lab catalogue. */}
            <Select
              label={t('visit.orders.service')}
              aria-label={t('visit.orders.service')}
              helperText={t('visit.orders.serviceScope', {
                type: labelFor(t, 'visit.orders.types', type),
              })}
              placeholder={t('visit.orders.choose')}
              value={draft.catalogId}
              onChange={(e) => setDraft((d) => ({ ...d, catalogId: e.target.value }))}
              options={forType.map((c) => ({
                // A clinic's own row is marked, because only one of the two
                // lists is theirs to get corrected.
                label: c.source === 'clinic' ? `${c.name} ★` : c.name,
                value: c.id,
              }))}
            />
            <Select
              label={t('visit.orders.urgency')}
              aria-label={t('visit.orders.urgency')}
              placeholder=""
              value={draft.urgency}
              onChange={(e) => setDraft((d) => ({ ...d, urgency: e.target.value }))}
              options={ORDER_URGENCIES.map((u) => ({
                value: u, label: t(`visit.orders.urgencies.${u}`),
              }))}
            />
            <TextInput
              label={t('visit.orders.sessions')}
              aria-label={t('visit.orders.sessions')}
              type="number"
              min={1}
              dir="auto"
              value={draft.sessionCount}
              onChange={(e) => setDraft((d) => ({ ...d, sessionCount: e.target.value }))}
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <TextInput
              label={t('visit.orders.description')}
              aria-label={t('visit.orders.description')}
              dir={autoDir(draft.description)}
              value={draft.description}
              placeholder={chosen?.name || t('visit.orders.descriptionHint')}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <Textarea
              label={t('visit.orders.notes')}
              aria-label={t('visit.orders.notes')}
              rows={2}
              dir="auto"
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Button
              className="min-h-11"
              onClick={submit}
              disabled={saving || (!draft.catalogId && !draft.description.trim())}
            >
              {saving ? t('common.actions.saving') : t('visit.orders.add')}
            </Button>
            {/* COSTS PARSINA CREDITS — a button, never automatic. The same
                control the تشخیص tab carries, in the same words, because it is
                the same promise: the AI proposes, the dentist files. */}
            <Button
              className="min-h-11"
              variant="outline"
              onClick={suggest}
              disabled={suggesting}
              data-testid="visit-orders-suggest"
            >
              <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 5 }} />
              {suggesting ? t('visit.orders.suggesting') : t('visit.orders.suggest')}
            </Button>
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>
            {t('visit.ai.costsCredits')}
          </div>
        </div>
      )}

      {!editable && (
        <Alert variant="info" style={{ marginBottom: 12 }}>{t('visit.readOnly.note')}</Alert>
      )}

      {/* ── The AI suggestions — PROPOSALS, drawn apart from the record ──────
          Dashed borders and a heading of their own, the same separation تشخیص
          draws between its differential and its recorded diagnoses: nothing
          here is an order until the dentist presses «افزودن». */}
      {suggestError === 'credits' && (
        <Alert variant="info" title={t('errors.credits.title')} style={{ marginBottom: 12 }}>
          {t('errors.credits.body')}
        </Alert>
      )}
      {suggestError === 'unavailable' && (
        <Alert variant="warning" style={{ marginBottom: 12 }}>
          {t('visit.orders.suggestUnavailable')}
        </Alert>
      )}

      {suggestions && !suggestError && (
        <div style={{ marginBottom: 14 }} data-testid="visit-orders-suggestions">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            fontSize: 11.5, fontWeight: 800, color: C.textSecondary,
          }}>
            <Sparkles size={13} aria-hidden />
            {t('visit.orders.suggestions')}
            <IconButton
              variant="ghost"
              size={44}
              icon={<X size={13} />}
              label={t('visit.orders.suggestClose')}
              onClick={clearSuggestions}
              style={{ marginInlineStart: 'auto' }}
            />
          </div>

          {suggestions.rationale && (
            <div style={{
              fontSize: 12, color: C.textSecondary, lineHeight: 1.9, marginBottom: 8,
            }} dir="auto">
              {suggestions.rationale}
            </div>
          )}

          {suggestions.items.length === 0 ? (
            <span style={{ fontSize: 12, color: C.textMuted }}>
              {t('visit.orders.noSuggestions')}
            </span>
          ) : suggestions.items.map((item) => {
            const Icon = TYPE_ICON[item.orderType] || ClipboardList;
            // Already on this visit — computed from the ORDERS THEMSELVES rather
            // than from a local "added" set, so it survives the reload after a
            // write and is still right when the same order was filed by hand.
            const taken = rows.some((o) => (o.description || '').trim() === item.name.trim());
            return (
              <div
                key={item.key}
                style={{
                  border: `1px dashed ${C.border}`, borderRadius: 10,
                  padding: '9px 11px', marginBottom: 8, background: CT.surface,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={15} color={C.teal} aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      {labelFor(t, 'visit.orders.types', item.orderType)}
                      {item.category ? ` · ${item.category}` : ''}
                      {` · ${labelFor(t, 'visit.orders.urgencies', item.urgency)}`}
                    </div>
                  </div>
                  {editable && (
                    <Button
                      className="min-h-11"
                      variant="subtle"
                      onClick={() => addSuggested(item)}
                      disabled={taken || !!addingKey}
                      data-testid={`visit-order-suggest-add-${item.key}`}
                    >
                      {taken
                        ? t('visit.orders.suggestAdded')
                        : addingKey === item.key
                          ? t('common.actions.saving')
                          : t('visit.orders.suggestAdd')}
                    </Button>
                  )}
                </div>
                {item.reason && (
                  <div
                    style={{ fontSize: 12, color: C.textSecondary, marginTop: 6, lineHeight: 1.8 }}
                    dir="auto"
                  >
                    {item.reason}
                  </div>
                )}
              </div>
            );
          })}

          {/* What the BFF refused because this clinic's dental catalogue does
              not carry the name. Said out loud: a dentist who spent a credit
              should be able to tell «پیشنهادی نیست» from «پیشنهاد بود، دندانی
              نبود». */}
          {suggestions.droppedCount > 0 && (
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              {t('visit.orders.suggestDropped', {
                count: formatNumber(suggestions.droppedCount),
              })}
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={40} color={C.textMuted} />}
          title={t('visit.orders.empty.title')}
          description={t('visit.orders.empty.description')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((o) => {
            const Icon = TYPE_ICON[o.orderType] || ClipboardList;
            const tone = STATUS_TONE[o.status] || STATUS_TONE.pending;
            const moves = TRANSITIONS[o.status] || [];
            return (
              <div
                key={o.id}
                style={{
                  border: `1px solid ${C.border}`, borderRadius: 10,
                  padding: '10px 12px', background: CT.surface,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon size={15} color={C.teal} aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                      {o.description}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      {labelFor(t, 'visit.orders.types', o.orderType)}
                      {o.urgency ? ` · ${labelFor(t, 'visit.orders.urgencies', o.urgency)}` : ''}
                      {o.sessionCount > 0
                        ? ` · ${t('visit.orders.sessionCount', { count: formatNumber(o.sessionCount) })}`
                        : ''}
                      {o.createdAt ? ` · ${formatDate(o.createdAt, 'numeric')}` : ''}
                    </div>
                  </div>
                  <span style={{
                    background: tone.bg, color: tone.ink, borderRadius: 8,
                    padding: '3px 9px', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
                  }}>
                    {labelFor(t, 'visit.orders.statuses', o.status)}
                  </span>
                </div>

                {o.notes && (
                  <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 6, lineHeight: 1.8 }}>
                    {o.notes}
                  </div>
                )}

                {/* Only the transitions the service will accept. Offering
                    «انجام شد» on a completed order is a button that always
                    errors — the state machine lives in the service and this
                    reads it rather than carrying a second copy. */}
                {editable && moves.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {moves.map((m) => (
                      <Button
                        key={m}
                        className="min-h-11"
                        variant={m === 'cancel' ? 'outline' : 'subtle'}
                        disabled={saving}
                        onClick={() => move(o.id, m)}
                      >
                        {t(`visit.orders.transitions.${m}`)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
